import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowExpandIcon,
  CircleFillIcon,
  EditIcon,
  MoreIcon,
} from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field, getFormValues, isValid } from 'redux-form';
import styled from 'styled-components';

import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { VersionMetadata, VersionState } from 'dashboard/components-lib/VersionsSection/utils';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import Tooltip from 'design-system/components/Tooltip';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import ImageDot from 'images/dashboard/dot.svg';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import useToggle from 'lib/hooks/useToggle';

const ICON_HEIGHT = 10;
const TOP_LINE_HEIGHT = 22;
const MARGIN_HEIGHT = 2;

const Row = styled.div.attrs(() => ({ variant: 'primary' }))`
  ${menuItemMixins.default};

  display: grid;
  grid-template-columns: 40px 1fr 60px;
  min-height: 76px;
  border-radius: 0;

  &:hover {
    ${menuItemMixins.hover};
  }

  &[aria-current='true'] {
    ${menuItemMixins.active};
  }
`;

const PlaceHolderTop = styled.div`
  height: ${TOP_LINE_HEIGHT}px;
`;

const TopLine = styled.div<{ selected: boolean }>`
  border-left: 3px solid ${Color.CoreSlate300};
  border-radius: 1px;
  height: ${TOP_LINE_HEIGHT - MARGIN_HEIGHT}px;
  margin-bottom: ${MARGIN_HEIGHT}px;

  ${({ selected, theme }) => selected && `border-color: ${theme.color.primary.brand}`}
`;

const BottomLine = styled.div<{ selected: boolean }>`
  border-left: 3px solid ${Color.CoreSlate300};
  border-radius: 1px;
  height: calc(100% - ${TOP_LINE_HEIGHT}px - ${ICON_HEIGHT}px - ${MARGIN_HEIGHT}px);
  margin-top: ${MARGIN_HEIGHT}px;

  ${({ selected, theme }) => selected && `border-color: ${theme.color.primary.brand}`}
`;

const IconContainer = styled.div`
  display: none;
  height: ${Spacing[4]};
`;

const ExpandableDescription = styled.div`
  display: flex;
  flex-direction: row;
  width: fit-content;

  &:hover > ${IconContainer} {
    transition-duration: ${TransitionDuration.Medium};
    display: block;
    svg {
      max-width: ${Spacing[3]};
      color: ${({ theme }) => theme.color.variants.basic.highlightDark};
    }
  }
`;

const StyledTextAreaField = styled(TextAreaField)`
  margin-bottom: 0px;
`;

const Dot = styled(ImageDot)<{ selected: boolean }>`
  height: ${ICON_HEIGHT}px;
  width: ${ICON_HEIGHT}px;
  color: ${Color.CoreSlate300};

  & > circle {
    stroke: none !important;
  }

  ${({ selected, theme }) => selected && `color: ${theme.color.primary.brand}`};
`;

const CenteredCircleFilled = styled(CircleFillIcon)`
  align-self: center;
  margin-right: ${Spacing[2]};
  height: 8px;
  width: 8px;
  path {
    fill: ${Color.CoreBlue700};
  }
`;

const LeftColumn = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const MiddleColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing[4]} 0;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: ${Spacing[4]};
  padding: ${Spacing[4]} 0;
`;

const Text = styled.span`
  ${paragraphMixins.md}
  font-weight: ${FontWeight.Medium};
  margin-right: ${Spacing[2]};
`;

const SecondaryText = styled.span`
  ${paragraphMixins.md}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
  color: ${({ theme }) => theme.color.text.tertiary};
  font-weight: ${FontWeight.Medium};
  margin-right: ${Spacing[2]};
`;

const TertiaryText = styled.span`
  ${paragraphMixins.md}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-right: ${Spacing[2]};
`;

const WrappedText = styled.span`
  ${paragraphMixins.md}
  white-space: pre-wrap;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const InnerRow = styled.div`
  display: flex;
  flex-direction: row;
`;

export interface StateStickerProps {
  versionState: VersionState;
}

const StateSticker = ({ versionState }: StateStickerProps) => {
  switch (versionState) {
    case VersionState.Live:
      return <Sticker variant='success'>Live</Sticker>;
    case VersionState.Unsaved:
      return <Sticker variant='danger'>Unsaved</Sticker>;
    case VersionState.Saved:
      return <Sticker variant='caution'>Saved</Sticker>;
    default:
      return <></>;
  }
};

interface VersionRowProps {
  version: VersionMetadata;
  isFirst: boolean;
  isLast: boolean;
  isSelected: boolean;
  formName: string;
  handleAddLocalDraftVersion: () => void;
  handleDiscardDraft: (() => Promise<void>) | null;
  handleSelectVersion: (versionId: string) => void;
  setDescription?: (description: string) => void;
}

const findDisplayDate = ({
  publishedAt,
  editedAt,
  updatedAt,
}: {
  // publishedAt is set when the version is published
  // editedAt is set when the version is edited in admin/dashboard (including published)
  // updatedAt is the activerecord updated at timestamp which may be set for any db updates
  //
  // theoretically once a version is published the edited_at should not change anymore. but just to be defensive
  // we take the max of (published_at, edited_at), and if neither of those exist, we fall back to updated_at.
  publishedAt: DateTime | null;
  editedAt: DateTime | null;
  updatedAt: DateTime | null;
}): { timestamp: string | null; explanation: string } => {
  if (publishedAt != null && (!editedAt || publishedAt >= editedAt)) {
    return {
      timestamp: publishedAt.toFormat(DATE_AND_TIME_FORMAT),
      explanation: 'Publish time',
    };
  }

  if (editedAt != null) {
    return {
      timestamp: editedAt.toFormat(DATE_AND_TIME_FORMAT),
      explanation: 'Last edited at',
    };
  }

  if (updatedAt != null) {
    return {
      timestamp: updatedAt.toFormat(DATE_AND_TIME_FORMAT),
      explanation: 'Last updated at',
    };
  }
  return {
    timestamp: null,
    explanation: 'Unknown',
  };
};

const DescriptionModal = ({
  onConfirm,
  onCancel,
  description,
  isEditable,
  isValid,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  description?: string;
  isEditable: boolean;
  isValid: boolean;
}) => {
  return (
    <ModalContainer>
      <ModalHeader onClose={onCancel}>Version Description</ModalHeader>
      <ModalBody>
        {isEditable ? (
          <>
            <Field
              id='description'
              name='description'
              type='text'
              component={StyledTextAreaField}
              placeholder='No description'
              autoFocus
            />
            <Spacer height={2} />
            <HStack>
              <Button
                id='version-section__description-editor-cancel'
                variant='basic'
                aria-label='Cancel'
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                id='version-section__description-editor-confirm'
                onClick={onConfirm}
                aria-label='Confirm'
                variant='primary'
                disabled={!isValid}
              >
                Confirm
              </Button>
            </HStack>
          </>
        ) : (
          <WrappedText>{description || 'No description'}</WrappedText>
        )}
      </ModalBody>
    </ModalContainer>
  );
};

const VersionDescription = ({
  id,
  description,
  isEditable,
  onClick,
}: {
  id: string;
  description: string;
  isEditable: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}) => {
  return (
    <ExpandableDescription id={`${id}__expand-version-description`} onClick={onClick}>
      <TertiaryText>{description || 'No description'}</TertiaryText>
      <IconContainer>{isEditable ? <EditIcon /> : <ArrowExpandIcon />}</IconContainer>
    </ExpandableDescription>
  );
};

export const VersionRow: FC<VersionRowProps> = ({
  version,
  isFirst,
  isLast,
  isSelected,
  formName,
  handleAddLocalDraftVersion,
  handleDiscardDraft,
  handleSelectVersion,
  setDescription,
}) => {
  const [
    showDiscardDraftModal,
    { prompt: prompt, confirm: confirmDiscardDraft, cancel: cancelDiscardDraft },
  ] = useConfirmation();

  const editorFormDescription = useSelector<RootState, any>(
    (state) => getFormValues(formName)(state) ?? {},
  ).description;

  const editorFormDescriptionValid = useSelector<RootState, any>(
    (state) => isValid(formName)(state) ?? true,
  );

  const [draftDescription, setDraftDescription] = useState(editorFormDescription);

  const dispatch = useDispatch();

  const [isOpenDescriptionModal, { enable: showDescriptionModal, disable: hideDescriptionModal }] =
    useToggle(false);

  const {
    id,
    description,
    'last-updater': lastUpdater,
    status,
    'updated-at': updatedAt,
    'edited-at': editedAt,
    'published-at': publishedAt,
  } = version;

  const { timestamp: updatedTimestampDisplay, explanation: updatedTimestampExplanation } =
    findDisplayDate({
      updatedAt,
      editedAt,
      publishedAt,
    });
  const isDraft = [VersionState.Saved, VersionState.Unsaved].includes(status);
  const isEditable = isSelected && version.editable;

  const moreItems = [
    {
      id: 'version-list__restore_version_button',
      label: 'Restore',
      variant: 'basic' as const,
      hidden: !isSelected || !version.restorable || version.status === VersionState.Archived,
      onClick: handleAddLocalDraftVersion,
    },
    {
      id: 'version-list__discard_draft_button',
      label: 'Discard',
      variant: 'danger' as const,
      hidden: !isSelected || !version.discardable,
      onClick: () => handleDiscardDraft && prompt(handleDiscardDraft),
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  const confirmDescriptionForm = () => {
    hideDescriptionModal();
    setDraftDescription(editorFormDescription);
    if (setDescription && isEditable) {
      setDescription(editorFormDescription);
    }
  };

  const cancelDescriptionForm = () => {
    hideDescriptionModal();
    dispatch(change(formName, 'description', draftDescription));
  };

  return (
    <>
      <ModalWrapper isOpen={showDiscardDraftModal} handleCloseModal={cancelDiscardDraft}>
        <ConfirmationModal
          cancelAction={cancelDiscardDraft}
          confirmAction={confirmDiscardDraft}
          header='Discard draft?'
          confirmActionText='Discard'
        >
          All unpublished changes in this draft will be discarded. This action is permanent and
          cannot be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={isOpenDescriptionModal} handleCloseModal={cancelDescriptionForm}>
        <DescriptionModal
          onConfirm={confirmDescriptionForm}
          onCancel={cancelDescriptionForm}
          description={description}
          isEditable={isEditable}
          isValid={editorFormDescriptionValid}
        />
      </ModalWrapper>
      <Row
        role='listitem'
        id={`${id}__version-history-row`}
        aria-current={isSelected}
        onClick={() => {
          if (!isSelected) {
            handleSelectVersion(id);
          }
        }}
      >
        <LeftColumn>
          {isFirst ? <PlaceHolderTop /> : <TopLine selected={isSelected} />}
          <Dot selected={isSelected} />
          {!isLast && <BottomLine selected={isSelected} />}
        </LeftColumn>
        <MiddleColumn>
          <InnerRow>
            <Text>{version.title}</Text>
            {updatedTimestampDisplay && (
              <Tooltip hideArrow trigger={<SecondaryText>{updatedTimestampDisplay}</SecondaryText>}>
                {updatedTimestampExplanation}
              </Tooltip>
            )}
            <StateSticker versionState={version.status} />
          </InnerRow>
          <VersionDescription
            id={version.id}
            description={
              // If the currently shown version is a draft, use the form description
              // here to capture description edits
              isSelected && isDraft && version.editable ? editorFormDescription : description
            }
            isEditable={isEditable}
            onClick={(e) => {
              e.stopPropagation();
              showDescriptionModal();
            }}
          />
          {lastUpdater && (
            <InnerRow>
              <CenteredCircleFilled />
              <TertiaryText>{lastUpdater}</TertiaryText>
            </InnerRow>
          )}
        </MiddleColumn>

        <RightColumn>
          <DropdownMenu
            trigger={
              <Button id='version-row__dropdown' variant='basic' width='icon' aria-label='More'>
                <MoreIcon />
              </Button>
            }
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                id={item.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  item.onClick();
                }}
                key={item.id}
                variant={item.variant}
                role='menuitem'
                aria-label={item.label}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        </RightColumn>
      </Row>
    </>
  );
};
